/**
 * Shared data-table wrapper used across feature screens for list, search, filter, selection,
 * sorting, pagination, and optional detail-drawer behavior.
 *
 * PARENT-CHILD CONTRACT:
 * Parents configure columns, rows, and optional toolbar/detail behaviors.
 * The table owns transient UI state such as the search query, drawer state, sorting, and paging,
 * then emits normalized events back to the parent when feature-level state must change.
 */
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, output, signal, AfterViewChecked, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { DrawerModule } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';

import { normalizeDrawerLabel } from '@shared/utils';
import { UiAutocompleteFieldComponent } from '../ui-autocomplete-field/ui-autocomplete-field.component';
import { UiAutocompleteOption } from '../ui-autocomplete-field/ui-autocomplete-field.types';
import { UiButtonComponent } from '../ui-button/ui-button.component';
import { UiDatepickerComponent } from '../ui-datepicker/ui-datepicker.component';
import { UiEmptyStateComponent } from '../ui-empty-state/ui-empty-state.component';
import { UiFormFieldComponent } from '../ui-form-field/ui-form-field.component';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';
import { UiResourceSummaryComponent } from '../ui-resource-summary/ui-resource-summary.component';
import { UiSelectComponent } from '../ui-select/ui-select.component';
import { UiSelectOption } from '../ui-select/ui-select.types';
import { UiToggleComponent } from '../ui-toggle/ui-toggle.component';
import {
  UiTableAction,
  UiTableActionClick,
  UiTableBadgeRule,
  UiTableBadgeTone,
  UiTableCellClick,
  UiTableColumn,
  UiTableDetailField,
  UiTableDetailMode,
  UiTableDetailSection,
  UiTableDetailTab,
  UiTableFilterField,
  UiTableFilterSearchChange,
  UiTableFilterValue,
  UiTableFirstColumnHoverMode,
  UiTableResourceSummaryItems,
  UiTableSelectChange,
  UiTableSelectionChange,
  UiTableSortChange,
  UiTableSortOrder,
  UiTableToggleChange
} from './ui-table.types';

type UiTableDrawerType = 'none' | 'filter' | 'detail';

/**
 * COMPONENT: Ui Table
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-table',
  standalone: true,
  imports: [FormsModule, TableModule, DrawerModule, SelectModule, ButtonModule, UiAutocompleteFieldComponent, UiButtonComponent, UiDatepickerComponent, UiEmptyStateComponent, UiFormFieldComponent, UiLabelTranslatePipe, UiResourceSummaryComponent, UiSelectComponent, UiToggleComponent],
  templateUrl: './ui-table.component.html',
  styleUrl: './ui-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTableComponent implements AfterViewChecked {
  private static readonly SUPPORTED_PAGE_SIZE_OPTIONS = [10, 15, 20, 50, 100] as const;
  private static readonly VISIBLE_BODY_ROW_LIMIT = 10;
  private readonly host = inject(ElementRef<HTMLElement>);

  // PARENT-CHILD CONTRACT: the parent defines row shape and visible table capabilities.
  readonly columns = input<UiTableColumn[]>([]);
  readonly data = input<unknown[]>([]);
  /** PARENT-CHILD CONTRACT: ariaLabel documents the input state boundary used by this file. */
  readonly ariaLabel = input('Data table');
  /** PARENT-CHILD CONTRACT: emptyMessage documents the input state boundary used by this file. */
  readonly emptyMessage = input('No records found.');

  /** PARENT-CHILD CONTRACT: showToolbar documents the input state boundary used by this file. */
  readonly showToolbar = input(true);
  /** PARENT-CHILD CONTRACT: showSearch documents the input state boundary used by this file. */
  readonly showSearch = input(true);
  /** PARENT-CHILD CONTRACT: showSort documents the input state boundary used by this file. */
  readonly showSort = input(true);
  /** PARENT-CHILD CONTRACT: showFilter documents the input state boundary used by this file. */
  readonly showFilter = input(true);
  /** PARENT-CHILD CONTRACT: showPagination documents the input state boundary used by this file. */
  readonly showPagination = input(false);
  readonly currentPage = input<number | null>(null);
  /** PARENT-CHILD CONTRACT: defaultPageSize documents the input state boundary used by this file. */
  readonly defaultPageSize = input(10);
  readonly pageSizeOptions = input<number[]>([10, 15, 20, 50, 100]);
  /** PARENT-CHILD CONTRACT: showPageSizeSelector documents the input state boundary used by this file. */
  readonly showPageSizeSelector = input(true);
  /** PARENT-CHILD CONTRACT: serverSide documents the input state boundary used by this file. */
  readonly serverSide = input(false);
  /** PARENT-CHILD CONTRACT: serverSearch documents the input state boundary used by this file. */
  readonly serverSearch = input(false);
  /** PARENT-CHILD CONTRACT: serverTotalCount documents the input state boundary used by this file. */
  readonly serverTotalCount = input(0);
  /** PARENT-CHILD CONTRACT: showSelection documents the input state boundary used by this file. */
  readonly showSelection = input(false);
  /** PARENT-CHILD CONTRACT: selectionKey documents the input state boundary used by this file. */
  readonly selectionKey = input('id');
  /** PARENT-CHILD CONTRACT: showBulkDeleteButton documents the input state boundary used by this file. */
  readonly showBulkDeleteButton = input(false);
  /** PARENT-CHILD CONTRACT: bulkDeleteLabel documents the input state boundary used by this file. */
  readonly bulkDeleteLabel = input('Delete All');
  /** PARENT-CHILD CONTRACT: showActionsColumn documents the input state boundary used by this file. */
  readonly showActionsColumn = input(false);
  /** PARENT-CHILD CONTRACT: actionsColumnLabel documents the input state boundary used by this file. */
  readonly actionsColumnLabel = input('Actions');
  readonly rowActions = input<UiTableAction[]>([
    { id: 'edit', label: 'Edit', variant: 'ghost' },
    { id: 'delete', label: 'Delete', variant: 'danger' }
  ]);
  /** PARENT-CHILD CONTRACT: searchPlaceholder documents the input state boundary used by this file. */
  readonly searchPlaceholder = input('Search...');
  readonly searchKeys = input<string[]>([]);
  readonly firstColumnHover = input<UiTableFirstColumnHoverMode>('hover');
  readonly badgeRules = input<Record<string, UiTableBadgeRule>>({
    active: { tone: 'green' },
    approved: { tone: 'green' },
    ok: { tone: 'green' },
    done: { tone: 'green' },
    completed: { tone: 'green' },
    inactive: { tone: 'neutral' },
    low: { tone: 'yellow' },
    medium: { tone: 'yellow' },
    pending: { tone: 'yellow' },
    'in-progress': { tone: 'blue' },
    review: { tone: 'blue' },
    rejected: { tone: 'red' },
    'out-of-stock': { tone: 'red' },
    overdue: { tone: 'red' },
    high: { tone: 'red' },
    field: { tone: 'yellow' },
    'field-agent': { tone: 'yellow' },
    agency: { tone: 'green' },
    'agency-agent': { tone: 'green' }
  });

  /** PARENT-CHILD CONTRACT: defaultSortKey documents the input state boundary used by this file. */
  readonly defaultSortKey = input('');
  readonly defaultSortOrder = input<UiTableSortOrder>('asc');

  readonly filterFields = input<UiTableFilterField[]>([]);

  /** PARENT-CHILD CONTRACT: rowClickable documents the input state boundary used by this file. */
  readonly rowClickable = input(false);
  /** PARENT-CHILD CONTRACT: detailTitleKey documents the input state boundary used by this file. */
  readonly detailTitleKey = input('name');
  readonly detailFields = input<UiTableDetailField[]>([]);
  readonly detailTabs = input<UiTableDetailTab[]>([]);
  readonly detailMode = input<UiTableDetailMode>('side-panel');

  readonly cellClick = output<UiTableCellClick>();
  readonly rowClick = output<unknown>();
  readonly actionClick = output<UiTableActionClick>();
  readonly toggleChange = output<UiTableToggleChange>();
  readonly selectChange = output<UiTableSelectChange>();
  readonly sortChange = output<UiTableSortChange>();
  readonly selectionChange = output<UiTableSelectionChange>();
  readonly bulkDelete = output<unknown[]>();
  readonly filtersAppliedChange = output<Record<string, UiTableFilterValue>>();
  readonly filtersReset = output<void>();
  readonly filterLoadMore = output<string>();
  readonly filterSearchChange = output<UiTableFilterSearchChange>();
  readonly searchChange = output<string>();
  readonly viewDocuments = output<unknown>();
  readonly pageChange = output<{ page: number; pageSize: number }>();

  // UI state owned locally by the table so every screen gets the same interaction model.
  readonly searchQuery = signal('');
  /** STATE OWNER: sortKey documents the signal state boundary used by this file. */
  readonly sortKey = signal('');
  readonly sortOrder = signal<UiTableSortOrder>('asc');
  readonly selectedRow = signal<unknown | null>(null);
  readonly selectedRowKeys = signal<string[]>([]);
  /** STATE OWNER: pageIndex documents the signal state boundary used by this file. */
  readonly pageIndex = signal(0);
  /** STATE OWNER: pageSize documents the signal state boundary used by this file. */
  readonly pageSize = signal(10);
  readonly activeDrawer = signal<UiTableDrawerType>('none');
  /** STATE OWNER: activeDetailTabId documents the signal state boundary used by this file. */
  readonly activeDetailTabId = signal('');
  readonly openDropdownRowKey = signal<string | null>(null);

  // Filters are split into draft vs applied state so the drawer can be edited before commit.
  readonly filterDraft = signal<Record<string, UiTableFilterValue>>({});
  readonly filterApplied = signal<Record<string, UiTableFilterValue>>({});
  /** STATE OWNER: appliedFilterCount documents the computed state boundary used by this file. */
  readonly appliedFilterCount = computed(() => {
    const state = this.filterApplied();
    let count = 0;

    for (const value of Object.values(state)) {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          count += 1;
        }

        continue;
      }

      if (typeof value === 'boolean') {
        if (value) {
          count += 1;
        }

        continue;
      }

      if (String(value).trim() !== '') {
        count += 1;
      }
    }

    return count;
  });

  /** STATE OWNER: hasActiveFilters documents the computed state boundary used by this file. */
  readonly hasActiveFilters = computed(() => this.appliedFilterCount() > 0);
  /** STATE OWNER: hasDataRows documents the computed state boundary used by this file. */
  readonly hasDataRows = computed(() => this.data().length > 0);
  /** STATE OWNER: shouldShowToolbar documents the computed state boundary used by this file. */
  readonly shouldShowToolbar = computed(() =>
    this.showToolbar()
    && (
      this.hasDataRows()
      || this.hasActiveFilters()
      || this.searchQuery().trim().length > 0
    )
  );

  /** STATE OWNER: sortableColumns documents the computed state boundary used by this file. */
  readonly sortableColumns = computed(() => this.columns().filter((column) => column.sortable));

  /**
   * Derives the client-side result set after applying the shared search, filter, and sort rules.
   * Parents can opt out of client paging by enabling server-side mode.
   */
  readonly processedRows = computed(() => {
    const query = this.serverSide() || this.serverSearch()
      ? ''
      : this.searchQuery().trim().toLowerCase();
    const applied = this.filterApplied();

    const searched = this.data().filter((row) => {
      const matchesSearch = !query || this.matchesSearch(row, query);
      const matchesFilter = this.matchesAllFilters(row, applied);
      return matchesSearch && matchesFilter;
    });

    const sortKey = this.activeSortKey();

    if (!sortKey) {
      return searched;
    }

    const order = this.sortOrder();

    return [...searched].sort((leftRow, rightRow) => {
      const left = this.valueOf(leftRow, sortKey);
      const right = this.valueOf(rightRow, sortKey);
      const result = String(left ?? '').localeCompare(String(right ?? ''), undefined, { numeric: true });
      return order === 'asc' ? result : -result;
    });
  });

  /** STATE OWNER: pagedRows documents the computed state boundary used by this file. */
  readonly pagedRows = computed(() => {
    if (this.serverSide()) {
      return this.data() as unknown[];
    }

    if (!this.showPagination()) {
      return this.processedRows();
    }

    const start = this.pageIndex() * this.pageSize();
    return this.processedRows().slice(start, start + this.pageSize());
  });

  /** STATE OWNER: pageSizeSelectorOptions documents the computed state boundary used by this file. */
  readonly pageSizeSelectorOptions = computed(() => this.resolvedPageSizeOptions());
  /** STATE OWNER: pageSizeValue documents the computed state boundary used by this file. */
  readonly pageSizeValue = computed(() => `${this.pageSize()}`);

  /** STATE OWNER: pageMeta documents the computed state boundary used by this file. */
  readonly pageMeta = computed(() => {
    const total = this.serverSide() ? this.serverTotalCount() : this.processedRows().length;

    if (total === 0) {
      return { total: 0, start: 0, end: 0 };
    }

    if (!this.showPagination()) {
      return { total, start: 1, end: total };
    }

    const start = this.pageIndex() * this.pageSize() + 1;
    const end = Math.min(total, this.pageIndex() * this.pageSize() + this.pageSize());
    return { total, start, end };
  });

  /** STATE OWNER: totalPages documents the computed state boundary used by this file. */
  readonly totalPages = computed(() => {
    if (!this.showPagination()) {
      return 1;
    }

    const total = this.serverSide() ? this.serverTotalCount() : this.processedRows().length;
    return Math.max(1, Math.ceil(total / this.pageSize()));
  });

  /** STATE OWNER: selectedRowTitle documents the computed state boundary used by this file. */
  readonly selectedRowTitle = computed(() => {
    const row = this.selectedRow();

    if (!row) {
      return '';
    }

    const value = this.valueOf(row, this.detailTitleKey());
    return value === null || value === undefined ? '' : String(value);
  });

  readonly resolvedDetailTabs = computed<UiTableDetailTab[]>(() => {
    const tabs = this.detailTabs();

    if (tabs.length > 0) {
      return tabs;
    }

    if (this.detailFields().length === 0) {
      return [];
    }

    return [
      {
        id: 'overview',
        label: 'Overview',
        fields: this.detailFields()
      }
    ];
  });

  /** STATE OWNER: activeDetailTab documents the computed state boundary used by this file. */
  readonly activeDetailTab = computed(() => {
    const tabs = this.resolvedDetailTabs();

    if (tabs.length === 0) {
      return null;
    }

    const active = this.activeDetailTabId();
    return tabs.find((tab) => tab.id === active) ?? tabs[0];
  });

  /** STATE OWNER: hasDetail documents the computed state boundary used by this file. */
  readonly hasDetail = computed(() => this.resolvedDetailTabs().length > 0);
  /** STATE OWNER: isRowClickable documents the computed state boundary used by this file. */
  readonly isRowClickable = computed(() => this.rowClickable());
  /** STATE OWNER: displayedRows documents the computed state boundary used by this file. */
  readonly displayedRows = computed(() => this.pagedRows());
  /**
   * BUSINESS LOGIC: drives the empty-state vs table-body switch. In server-side mode the backend
   * already applied search/filter/pagination, so visibility must follow the rendered rows
   * (`pagedRows`) rather than the client-side `processedRows`. Using `processedRows` here hid
   * server-filtered results because the client filter values (e.g. agency id, posType casing) did
   * not re-match the returned rows.
   */
  readonly hasVisibleRows = computed(() =>
    this.serverSide() ? this.pagedRows().length > 0 : this.processedRows().length > 0
  );
  /** STATE OWNER: firstDataColumnKey documents the computed state boundary used by this file. */
  readonly firstDataColumnKey = computed(() => this.columns()[0]?.key ?? '');
  /** STATE OWNER: isFilterDrawerOpen documents the computed state boundary used by this file. */
  readonly isFilterDrawerOpen = computed(() => this.activeDrawer() === 'filter');
  /** STATE OWNER: isDetailDrawerOpen documents the computed state boundary used by this file. */
  readonly isDetailDrawerOpen = computed(() => this.activeDrawer() === 'detail');
  /** STATE OWNER: isAnyDrawerOpen documents the computed state boundary used by this file. */
  readonly isAnyDrawerOpen = computed(() => this.activeDrawer() !== 'none');
  /** STATE OWNER: shouldScrollBody documents the computed state boundary used by this file. */
  readonly shouldScrollBody = computed(() =>
    this.showPagination() || this.pagedRows().length > UiTableComponent.VISIBLE_BODY_ROW_LIMIT
  );

  /** STATE OWNER: selectedRows documents the computed state boundary used by this file. */
  readonly selectedRows = computed(() => {
    const selected = new Set(this.selectedRowKeys());
    return this.data().filter((row) => selected.has(this.rowKey(row)));
  });

  /** STATE OWNER: allDisplayedSelected documents the computed state boundary used by this file. */
  readonly allDisplayedSelected = computed(() => {
    const displayed = this.displayedRows();

    if (displayed.length === 0) {
      return false;
    }

    const selected = new Set(this.selectedRowKeys());
    return displayed.every((row) => selected.has(this.rowKey(row)));
  });

  readonly normalizeDrawerLabel = normalizeDrawerLabel;

  /**
   * Seeds shared defaults so every feature screen starts from the same sorting and page-size rules.
   */
  constructor() {
    const initialSortKey = this.defaultSortKey();
    const fallbackSortKey = this.sortableColumns()[0]?.key ?? '';
    const resolvedSortKey = initialSortKey || fallbackSortKey;

    this.sortKey.set(resolvedSortKey);
    this.sortOrder.set(this.defaultSortOrder());
    // BUSINESS LOGIC: seed the initial page size from the resolved default so server-side lists,
    // emitted page events, and visible pagination all agree on first render.
    this.pageSize.set(this.resolveInitialPageSize());

    /** STATE OWNER: reactiveEffect documents the effect state boundary used by this file. */
    effect(() => {
      const availablePageSizes = this.resolvedPageSizeOptions();
      const currentPageSize = this.pageSize();
      const resolvedPageSize = availablePageSizes.includes(currentPageSize)
        ? currentPageSize
        : this.resolveInitialPageSize();

      if (currentPageSize !== resolvedPageSize) {
        this.pageSize.set(resolvedPageSize);
        this.pageIndex.set(0);
      }
    });

    /** STATE OWNER: reactiveEffect documents the effect state boundary used by this file. */
    effect(() => {
      const currentPage = this.currentPage();
      if (!this.showPagination() || !Number.isFinite(currentPage) || (currentPage ?? 0) <= 0) {
        return;
      }

      const nextPageIndex = Math.floor((currentPage as number) - 1);
      if (this.pageIndex() !== nextPageIndex) {
        this.pageIndex.set(nextPageIndex);
      }
    });
  }

  /**
   * BUSINESS LOGIC: ngAfterViewChecked
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  ngAfterViewChecked(): void {
    this.syncMeasuredViewportHeight();
  }

  /**
   * Updates local search state and informs the parent when a server-side query should be reloaded.
   */
  onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.pageIndex.set(0);
    this.searchChange.emit(value);
  }

  /**
   * BUSINESS LOGIC: openFilterPanel
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  openFilterPanel(): void {
    this.filterDraft.set({ ...this.filterApplied() });
    this.selectedRow.set(null);
    this.activeDrawer.set('filter');
  }

  /**
   * BUSINESS LOGIC: closeFilterPanel
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  closeFilterPanel(): void {
    if (this.activeDrawer() === 'filter') {
      this.activeDrawer.set('none');
    }
  }

  /**
   * BUSINESS LOGIC: syncMeasuredViewportHeight
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private syncMeasuredViewportHeight(): void {
    const host = this.host.nativeElement;
    const headRow = host.querySelector('.ui-table thead tr') as HTMLElement | null;
    const bodyRow =
      (host.querySelector('.ui-table tbody tr:not(.ui-table__empty-row)') as HTMLElement | null) ??
      (host.querySelector('.ui-table tbody tr') as HTMLElement | null);

    if (!headRow || !bodyRow) {
      return;
    }

    const headHeight = Math.ceil(headRow.getBoundingClientRect().height);
    const rowHeight = Math.ceil(bodyRow.getBoundingClientRect().height);

    if (headHeight > 0) {
      host.style.setProperty('--ui-table-head-height', `${headHeight}px`);
    }

    if (rowHeight > 0) {
      host.style.setProperty('--ui-table-row-height', `${rowHeight}px`);
    }
  }

  /**
   * BUSINESS LOGIC: openDetailPanel
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  openDetailPanel(row: unknown): void {
    if (!this.hasDetail() || this.detailMode() === 'none') {
      return;
    }

    this.selectedRow.set(row);

    const firstTab = this.resolvedDetailTabs()[0];
    this.activeDetailTabId.set(firstTab?.id ?? '');
    this.activeDrawer.set('detail');
  }

  /**
   * BUSINESS LOGIC: closeDetailPanel
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  closeDetailPanel(): void {
    if (this.activeDrawer() === 'detail') {
      this.activeDrawer.set('none');
    }

    this.selectedRow.set(null);
  }

  /**
   * BUSINESS LOGIC: closeActiveDrawer
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  closeActiveDrawer(): void {
    const current = this.activeDrawer();

    if (current === 'filter') {
      this.closeFilterPanel();
      return;
    }

    if (current === 'detail') {
      this.closeDetailPanel();
    }
  }

  /**
   * BUSINESS LOGIC: selectDetailTab
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  selectDetailTab(tabId: string): void {
    this.activeDetailTabId.set(tabId);
  }

  /**
   * Updates draft filter text without applying it immediately.
   * PARENT-CHILD CONTRACT: dependent reset keys let feature screens model chained filter fields.
   */
  setFilterDraftText(fieldKey: string, value: string): void {
    const field = this.filterFields().find((item) => item.key === fieldKey);

    this.filterDraft.update((state) => {
      const nextState: Record<string, UiTableFilterValue> = { ...state, [fieldKey]: value };

      for (const resetKey of field?.resetKeysOnChange ?? []) {
        nextState[resetKey] = '';
      }

      return nextState;
    });
  }

  /**
   * BUSINESS LOGIC: setFilterDraftValues
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  setFilterDraftValues(fieldKey: string, values: string[]): void {
    this.filterDraft.update((state) => ({ ...state, [fieldKey]: values }));
  }

  /**
   * BUSINESS LOGIC: setFilterDraftToggle
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  setFilterDraftToggle(fieldKey: string, value: boolean): void {
    this.filterDraft.update((state) => ({ ...state, [fieldKey]: value }));
  }

  /**
   * Commits the current filter draft and emits one normalized filter payload for the parent.
   */
  applyFilters(): void {
    const nextFilters = { ...this.filterDraft() };
    this.filterApplied.set(nextFilters);
    this.filtersAppliedChange.emit(nextFilters);
    this.pageIndex.set(0);
    this.closeFilterPanel();
  }

  /**
   * BUSINESS LOGIC: resetFilters
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  resetFilters(): void {
    this.filterDraft.set({});
    this.filterApplied.set({});
    this.filtersAppliedChange.emit({});
    this.filtersReset.emit();
    this.pageIndex.set(0);
    this.closeFilterPanel();
  }

  /**
   * BUSINESS LOGIC: toggleColumnSort
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  toggleColumnSort(column: UiTableColumn): void {
    if (!column.sortable) {
      return;
    }

    if (this.sortKey() === column.key) {
      this.sortOrder.set(this.sortOrder() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortKey.set(column.key);
      this.sortOrder.set('asc');
    }

    this.sortChange.emit({ key: this.sortKey(), order: this.sortOrder() });
    this.pageIndex.set(0);
  }

  /**
   * BUSINESS LOGIC: headerSortState
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  headerSortState(column: UiTableColumn): 'ascending' | 'descending' | 'none' {
    if (!column.sortable || !this.showSort()) {
      return 'none';
    }

    if (this.sortKey() !== column.key) {
      return 'none';
    }

    return this.sortOrder() === 'asc' ? 'ascending' : 'descending';
  }

  /**
   * BUSINESS LOGIC: onSortHeaderKeydown
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onSortHeaderKeydown(event: KeyboardEvent, column: UiTableColumn): void {
    if (!column.sortable || !this.showSort()) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleColumnSort(column);
    }
  }

  /**
   * BUSINESS LOGIC: badgeToneFor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  badgeToneFor(raw: string): UiTableBadgeTone {
    const rule = this.badgeRules()[this.normalizeBadgeKey(raw)];
    return rule?.tone ?? 'neutral';
  }

  /**
   * BUSINESS LOGIC: badgeBgFor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  badgeBgFor(raw: string): string | null {
    const rule = this.badgeRules()[this.normalizeBadgeKey(raw)];
    return rule?.colors ? `var(${rule.colors.backgroundVar})` : null;
  }

  /**
   * BUSINESS LOGIC: badgeTextFor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  badgeTextFor(raw: string): string | null {
    const rule = this.badgeRules()[this.normalizeBadgeKey(raw)];
    return rule?.colors ? `var(${rule.colors.textVar})` : null;
  }

  /**
   * BUSINESS LOGIC: onDataCellClick
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onDataCellClick(event: Event, column: UiTableColumn, row: unknown): void {
    const target = event.target as HTMLElement | null;

    if (target && this.isInteractiveTarget(target)) {
      return;
    }

    if (!this.isFirstColumnInteractive(column)) {
      return;
    }

    this.emitFirstColumnClick(column, row);
  }

  /**
   * BUSINESS LOGIC: toggleAllDisplayed
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  toggleAllDisplayed(checked: boolean): void {
    const selected = new Set(this.selectedRowKeys());

    if (checked) {
      this.displayedRows().forEach((row) => selected.add(this.rowKey(row)));
    } else {
      this.displayedRows().forEach((row) => selected.delete(this.rowKey(row)));
    }

    this.selectedRowKeys.set([...selected]);
    this.selectionChange.emit({ selectedRows: this.selectedRows() });
  }

  /**
   * BUSINESS LOGIC: toggleRowSelection
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  toggleRowSelection(event: Event, row: unknown, checked: boolean): void {
    event.stopPropagation();

    const key = this.rowKey(row);
    const selected = new Set(this.selectedRowKeys());

    if (checked) {
      selected.add(key);
    } else {
      selected.delete(key);
    }

    this.selectedRowKeys.set([...selected]);
    this.selectionChange.emit({ selectedRows: this.selectedRows() });
  }

  /**
   * BUSINESS LOGIC: isRowSelected
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isRowSelected(row: unknown): boolean {
    return this.selectedRowKeys().includes(this.rowKey(row));
  }

  /**
   * BUSINESS LOGIC: triggerBulkDelete
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  triggerBulkDelete(): void {
    this.bulkDelete.emit(this.selectedRows());
  }

  /**
   * BUSINESS LOGIC: setPageSize
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  setPageSize(value: string): void {
    const parsed = Number(value);
    const supportedSizes = this.resolvedPageSizeOptions();

    if (!Number.isFinite(parsed) || !supportedSizes.includes(parsed)) {
      return;
    }

    this.pageSize.set(parsed);
    this.pageIndex.set(0);

    if (this.serverSide()) {
      this.pageChange.emit({ page: 1, pageSize: parsed });
    }
  }

  /**
   * BUSINESS LOGIC: onPageSizeSelectChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onPageSizeSelectChange(event: Event): void {
    const select = event.target as HTMLSelectElement | null;
    this.setPageSize(select?.value ?? '');
  }

  /**
   * BUSINESS LOGIC: previousPage
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  previousPage(): void {
    const next = this.pageIndex() - 1;
    this.pageIndex.set(next < 0 ? 0 : next);

    if (this.serverSide()) {
      this.pageChange.emit({ page: this.pageIndex() + 1, pageSize: this.pageSize() });
    }
  }

  /**
   * BUSINESS LOGIC: nextPage
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  nextPage(): void {
    const max = this.totalPages() - 1;
    const next = this.pageIndex() + 1;
    this.pageIndex.set(next > max ? max : next);

    if (this.serverSide()) {
      this.pageChange.emit({ page: this.pageIndex() + 1, pageSize: this.pageSize() });
    }
  }

  /**
   * BUSINESS LOGIC: canGoPrevious
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  canGoPrevious(): boolean {
    return this.pageIndex() > 0;
  }

  /**
   * BUSINESS LOGIC: canGoNext
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  canGoNext(): boolean {
    return this.pageIndex() < this.totalPages() - 1;
  }

  /**
   * BUSINESS LOGIC: onCellClick
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onCellClick(event: Event, column: UiTableColumn, row: unknown): void {
    event.stopPropagation();

    if (!column.clickable || !this.isFirstColumn(column)) {
      return;
    }

    this.emitFirstColumnClick(column, row);
  }

  /**
   * BUSINESS LOGIC: onActionClick
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onActionClick(event: Event, actionId: string, row: unknown): void {
    event.stopPropagation();
    this.actionClick.emit({ actionId, row });
  }

  /**
   * BUSINESS LOGIC: onToggleChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onToggleChange(column: UiTableColumn, row: unknown, checked: boolean): void {
    this.toggleChange.emit({ key: column.key, row, checked });
  }

  /**
   * BUSINESS LOGIC: onSelectChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onSelectChange(column: UiTableColumn, row: unknown, value: string): void {
    this.selectChange.emit({ key: column.key, row, value });
  }

  /**
   * BUSINESS LOGIC: isIconOnlyAction
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isIconOnlyAction(action: UiTableAction): boolean {
    return Boolean(this.actionIconSymbol(action));
  }

  /**
   * BUSINESS LOGIC: actionIconSymbol
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  actionIconSymbol(action: UiTableAction): string {
    if (action.iconSymbol) {
      return action.iconSymbol.trim();
    }

    const kind = this.actionIconKind(action);

    if (kind === 'edit') {
      return 'edit';
    }

    if (kind === 'delete') {
      return 'delete';
    }

    if (kind === 'menu') {
      return 'more_vert';
    }

    if (kind === 'approve') {
      return 'done';
    }

    if (kind === 'reject') {
      return 'close';
    }

    return '';
  }

  /**
   * BUSINESS LOGIC: sortIconSymbol
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  sortIconSymbol(column: UiTableColumn): string {
    switch (this.headerSortState(column)) {
      case 'ascending':
        return 'arrow_upward';
      case 'descending':
        return 'arrow_downward';
      default:
        return 'arrow_upward';
    }
  }

  /**
   * BUSINESS LOGIC: actionIconKind
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private actionIconKind(action: UiTableAction): 'edit' | 'delete' | 'menu' | 'approve' | 'reject' | null {
    const key = action.id.trim().toLowerCase();

    if (key === 'edit' || key.startsWith('edit-') || key.endsWith('-edit')) {
      return 'edit';
    }

    if (key === 'delete' || key.startsWith('delete-') || key.endsWith('-delete')) {
      return 'delete';
    }

    if (key === 'menu' || key === 'manage' || key.startsWith('manage-') || key.endsWith('-menu')) {
      return 'menu';
    }

    if (key.startsWith('approve-') || key.endsWith('-approve') || key === 'approve') {
      return 'approve';
    }

    if (key.startsWith('reject-') || key.endsWith('-reject') || key === 'reject') {
      return 'reject';
    }

    return null;
  }

  /**
   * BUSINESS LOGIC: trackRow
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  trackRow(index: number, row: unknown): unknown {
    if (typeof row !== 'object' || row === null) {
      return index;
    }

    const id = (row as Record<string, unknown>)['id'];
    return id ?? index;
  }

  /**
   * BUSINESS LOGIC: rowKey
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  rowKey(row: unknown): string {
    const key = this.selectionKey();
    const value = this.valueOf(row, key);

    return value === null || value === undefined ? String(this.trackRow(0, row)) : String(value);
  }

  /**
   * BUSINESS LOGIC: valueOf
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  valueOf(row: unknown, key: string): unknown {
    if (typeof row !== 'object' || row === null) {
      return '';
    }

    return (row as Record<string, unknown>)[key];
  }

  getDocuments(row: unknown): Array<{ id: string; name: string; type: string; url: string; downloadUrl?: string }> {
    if (typeof row !== 'object' || row === null) return [];
    const docs = (row as Record<string, unknown>)['documents'];
    if (!Array.isArray(docs)) return [];
    return docs as Array<{ id: string; name: string; type: string; url: string }>;
  }

  /**
   * BUSINESS LOGIC: getDetailTableRows
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  getDetailTableRows(row: unknown, field: UiTableDetailField): unknown[] {
    if (field.tableRows) return field.tableRows(row);
    if (typeof row !== 'object' || row === null) return [];
    const value = (row as Record<string, unknown>)[field.key];
    return Array.isArray(value) ? value : [];
  }

  /**
   * BUSINESS LOGIC: resourceSummaryItemsOf
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  resourceSummaryItemsOf(row: unknown, column: UiTableColumn): UiTableResourceSummaryItems {
    const value = this.valueOf(row, column.key);
    return Array.isArray(value) ? value as UiTableResourceSummaryItems : [];
  }

  /**
   * BUSINESS LOGIC: detailTableBadgeToneFor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  detailTableBadgeToneFor(raw: string, field: UiTableDetailField): UiTableBadgeTone {
    const rules = field.tableBadgeRules ?? this.badgeRules();
    const rule = rules[this.normalizeBadgeKey(raw)];
    return (rule as UiTableBadgeRule | undefined)?.tone ?? 'neutral';
  }

  /**
   * BUSINESS LOGIC: textOf
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  textOf(row: unknown, column: UiTableColumn): string {
    const value = this.valueOf(row, column.key);

    if (column.formatter) {
      return column.formatter(value, row);
    }

    return value === null || value === undefined ? '' : String(value);
  }

  /**
   * BUSINESS LOGIC: detailTextOf
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  detailTextOf(row: unknown, field: UiTableDetailField): string {
    const value = this.valueOf(row, field.key);

    if (field.formatter) {
      return field.formatter(value, row);
    }

    return value === null || value === undefined ? '' : String(value);
  }

  /**
   * BUSINESS LOGIC: detailSectionsFor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  detailSectionsFor(tab: UiTableDetailTab): UiTableDetailSection[] {
    if (tab.sections && tab.sections.length > 0) {
      return tab.sections;
    }

    if (tab.fields && tab.fields.length > 0) {
      return [
        {
          id: `${tab.id}-default`,
          fields: tab.fields
        }
      ];
    }

    return [];
  }

  /**
   * BUSINESS LOGIC: filterValue
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  filterValue(fieldKey: string): UiTableFilterValue {
    return this.filterDraft()[fieldKey] ?? '';
  }

  /**
   * BUSINESS LOGIC: filterValues
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  filterValues(fieldKey: string): string[] {
    const value = this.filterValue(fieldKey);
    return Array.isArray(value) ? value : [];
  }

  /**
   * BUSINESS LOGIC: asText
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  asText(value: UiTableFilterValue): string {
    return String(value);
  }

  filterOptions(field: UiTableFilterField): Array<{ label: string; value: string; caption?: string }> {
    const options = field.options;

    if (!options) {
      return [];
    }

    return typeof options === 'function' ? options(this.filterDraft()) : options;
  }

  /**
   * BUSINESS LOGIC: filterAutocompleteOptions
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  filterAutocompleteOptions(field: UiTableFilterField): UiAutocompleteOption[] {
    return this.filterOptions(field).map((option) => ({
      label: option.label,
      value: option.value,
      caption: option.caption
    }));
  }

  /**
   * BUSINESS LOGIC: isFilterFieldDisabled
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isFilterFieldDisabled(field: UiTableFilterField): boolean {
    const disabled = field.disabled;
    return typeof disabled === 'function' ? disabled(this.filterDraft()) : Boolean(disabled);
  }

  /**
   * BUSINESS LOGIC: isActionDisabled
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isActionDisabled(action: { disabled?: (row: unknown) => boolean }, row: unknown): boolean {
    return action.disabled ? action.disabled(row) : false;
  }

  /**
   * BUSINESS LOGIC: isToggleChecked
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isToggleChecked(column: UiTableColumn, row: unknown): boolean {
    return column.toggle?.checked ? column.toggle.checked(row) : false;
  }

  /**
   * BUSINESS LOGIC: isToggleDisabled
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isToggleDisabled(column: UiTableColumn, row: unknown): boolean {
    return column.toggle?.disabled ? column.toggle.disabled(row) : false;
  }

  /**
   * BUSINESS LOGIC: toggleAriaLabel
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  toggleAriaLabel(column: UiTableColumn, row: unknown): string {
    return column.toggle?.ariaLabel ? column.toggle.ariaLabel(row) : column.label;
  }

  /**
   * BUSINESS LOGIC: selectOptions
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  selectOptions(column: UiTableColumn, row: unknown): UiSelectOption[] {
    if (!column.select?.options) {
      return [];
    }

    return typeof column.select.options === 'function' ? column.select.options(row) : column.select.options;
  }

  /**
   * BUSINESS LOGIC: selectValue
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  selectValue(column: UiTableColumn, row: unknown): string {
    if (column.select?.value) {
      return column.select.value(row);
    }

    const value = this.valueOf(row, column.key);
    return value === null || value === undefined ? '' : String(value);
  }

  /**
   * BUSINESS LOGIC: loadMoreFilterOptions
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  loadMoreFilterOptions(field: UiTableFilterField): void {
    this.filterLoadMore.emit(field.key);
  }

  /**
   * BUSINESS LOGIC: onFilterSearchChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onFilterSearchChange(field: UiTableFilterField, query: string): void {
    this.filterSearchChange.emit({ key: field.key, query });
  }

  /**
   * BUSINESS LOGIC: isSelectDisabled
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isSelectDisabled(column: UiTableColumn, row: unknown): boolean {
    return column.select?.disabled ? column.select.disabled(row) : false;
  }

  /**
   * BUSINESS LOGIC: activeSortKey
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  activeSortKey(): string {
    const key = this.sortKey();

    if (key) {
      return key;
    }

    return this.defaultSortKey() || this.sortableColumns()[0]?.key || '';
  }

  /**
   * BUSINESS LOGIC: matchesSearch
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private matchesSearch(row: unknown, query: string): boolean {
    const keys = this.searchKeys();

    if (keys.length > 0) {
      return keys.some((key) => String(this.valueOf(row, key)).toLowerCase().includes(query));
    }

    return this.columns().some((column) => String(this.valueOf(row, column.key)).toLowerCase().includes(query));
  }

  /**
   * BUSINESS LOGIC: matchesAllFilters
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private matchesAllFilters(row: unknown, filters: Record<string, UiTableFilterValue>): boolean {
    const fields = this.filterFields();

    return fields.every((field) => {
      const filterValue = filters[field.key];

      if (field.type === 'toggle') {
        if (filterValue !== true) {
          return true;
        }

        return Boolean(this.valueOf(row, field.key));
      }

      if (filterValue === undefined || filterValue === '') {
        return true;
      }

      const rowValue = this.valueOf(row, field.key);

      if (field.type === 'multiselect') {
        if (!Array.isArray(filterValue) || filterValue.length === 0) {
          return true;
        }

        return filterValue.includes(String(rowValue ?? ''));
      }

      if (field.type === 'text' || field.type === 'autocomplete') {
        return String(rowValue ?? '').toLowerCase().includes(String(filterValue).toLowerCase());
      }

      if (field.type === 'number') {
        return Number(rowValue) === Number(filterValue);
      }

      if (field.type === 'date') {
        return String(rowValue ?? '') === String(filterValue);
      }

      return String(rowValue ?? '') === String(filterValue);
    });
  }

  /**
   * BUSINESS LOGIC: normalizeBadgeKey
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeBadgeKey(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, '-');
  }

  /**
   * BUSINESS LOGIC: isFirstColumn
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isFirstColumn(column: UiTableColumn): boolean {
    return column.key === this.firstDataColumnKey();
  }

  /**
   * BUSINESS LOGIC: shouldShowFirstColumnHover
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  shouldShowFirstColumnHover(column: UiTableColumn): boolean {
    return this.firstColumnHover() === 'hover' && this.isFirstColumn(column);
  }

  /**
   * BUSINESS LOGIC: isFirstColumnInteractive
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isFirstColumnInteractive(column: UiTableColumn): boolean {
    return this.isFirstColumn(column) && (column.clickable === true || this.isRowClickable());
  }

  /**
   * BUSINESS LOGIC: emitFirstColumnClick
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private emitFirstColumnClick(column: UiTableColumn, row: unknown): void {
    this.cellClick.emit({ key: column.key, row });

    if (this.hasDetail() && this.detailMode() === 'side-panel') {
      this.openDetailPanel(row);
    }
  }

  /**
   * BUSINESS LOGIC: resolveInitialPageSize
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private resolveInitialPageSize(): number {
    const options = this.resolvedPageSizeOptions();
    const defaultSize = this.defaultPageSize();

    if (defaultSize > 0 && options.includes(defaultSize)) {
      return defaultSize;
    }

    return options[0] ?? 10;
  }

  /**
   * BUSINESS LOGIC: resolvedPageSizeOptions
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private resolvedPageSizeOptions(): number[] {
    // Keep shared page-size options strict while preserving the project default of 10 rows.
    const supported = new Set<number>(UiTableComponent.SUPPORTED_PAGE_SIZE_OPTIONS);
    const requested = this.pageSizeOptions().filter((value) => supported.has(value));
    const uniqueRequested = [...new Set(requested)];

    if (!uniqueRequested.includes(10)) {
      uniqueRequested.push(10);
    }

    if (uniqueRequested.length === 0) {
      return [10, 15, 20, 50, 100];
    }

    return uniqueRequested.sort((left, right) => left - right);
  }

  /**
   * BUSINESS LOGIC: visibleActions
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  visibleActions(actions: UiTableAction[]): UiTableAction[] {
    if (actions.length <= 2) return actions;
    return actions.filter((a) => {
      const k = a.id.trim().toLowerCase();
      return k === 'edit' || k.startsWith('edit-') || k.endsWith('-edit') ||
             k === 'delete' || k.startsWith('delete-') || k.endsWith('-delete');
    }).slice(0, 2);
  }

  /**
   * BUSINESS LOGIC: overflowActions
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  overflowActions(actions: UiTableAction[]): UiTableAction[] {
    if (actions.length <= 2) return [];
    const visible = new Set(this.visibleActions(actions).map((a) => a.id));
    return actions.filter((a) => !visible.has(a.id));
  }

  /**
   * BUSINESS LOGIC: hasOverflowActions
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  hasOverflowActions(actions: UiTableAction[]): boolean {
    return this.overflowActions(actions).length > 0;
  }

  /**
   * BUSINESS LOGIC: isCompactTwoActionLayout
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isCompactTwoActionLayout(actions: UiTableAction[]): boolean {
    return this.visibleActions(actions).length === 2 && !this.hasOverflowActions(actions);
  }

  /**
   * BUSINESS LOGIC: isOverflowTriggerDisabled
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isOverflowTriggerDisabled(actions: UiTableAction[], row: unknown): boolean {
    return actions.length > 0 && actions.every((action) => this.isActionDisabled(action, row));
  }

  /**
   * BUSINESS LOGIC: isDropdownOpen
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isDropdownOpen(row: unknown): boolean {
    return this.openDropdownRowKey() === this.rowKey(row);
  }

  /**
   * BUSINESS LOGIC: toggleDropdown
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  toggleDropdown(event: Event, row: unknown, actions: UiTableAction[]): void {
    event.stopPropagation();
    if (this.isOverflowTriggerDisabled(actions, row)) {
      return;
    }
    const key = this.rowKey(row);
    this.openDropdownRowKey.set(this.openDropdownRowKey() === key ? null : key);
  }

  @HostListener('document:click')
  /**
   * BUSINESS LOGIC: onDocumentClick
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onDocumentClick(): void {
    this.openDropdownRowKey.set(null);
  }

  /**
   * BUSINESS LOGIC: closeDropdown
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  closeDropdown(): void {
    this.openDropdownRowKey.set(null);
  }

  /**
   * BUSINESS LOGIC: onDropdownActionClick
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onDropdownActionClick(event: Event, actionId: string, row: unknown): void {
    this.closeDropdown();
    this.onActionClick(event, actionId, row);
  }

  /**
   * BUSINESS LOGIC: isInteractiveTarget
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private isInteractiveTarget(target: HTMLElement): boolean {
    return Boolean(
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('textarea') ||
      target.closest('.ui-table__actions') ||
      target.closest('.ui-table__selection-cell') ||
      target.closest('.ui-table__link') ||
      target.closest('.ui-table__overflow-dropdown') ||
      target.closest('ui-toggle') ||
      target.closest('.ui-toggle')
    );
  }
}
